import React, { useEffect, useState } from 'react';
import { Panel, Icon } from '../../components/ui';
import { startOutreachRun } from '../../utils/api';
import { RUN_MODES } from '../utils';

// Starts a research run. The request returns as soon as the workflow is queued; pages poll
// the run afterwards. Full AI reanalysis needs an explicit cost confirmation.
export default function RunResearchModal({ onClose, onStarted, emailDefault = false, costCapUsd }) {
  const [mode, setMode] = useState('incremental');
  const [notifyByEmail, setNotifyByEmail] = useState(Boolean(emailDefault));
  const [confirmReanalysis, setConfirmReanalysis] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'full_reanalysis' && !confirmReanalysis) { setError('Tick the confirmation box to run a full AI reanalysis.'); return; }
    setBusy(true);
    try {
      const { run } = await startOutreachRun({ mode, notifyByEmail, confirmReanalysis: mode === 'full_reanalysis' ? confirmReanalysis : undefined });
      onStarted(run);
    } catch (err) {
      if (err.status === 409) setError(err.message || 'A research run is already in progress. Wait for it to finish.');
      else setError(err.message || 'Could not start the research run.');
    } finally { setBusy(false); }
  };

  return (
    <div className="ops-drawer__backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Run research" style={{ width: '100%', maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <form onSubmit={submit}>
          <Panel title="Run research" foot={(
            <div className="ops-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="ops-btn ops-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="ops-btn ops-btn--primary" disabled={busy}><Icon name="play" size={14} /> {busy ? 'Starting' : 'Start run'}</button>
            </div>
          )}>
            <div className="ops-form-section">
              <h3>Mode</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {RUN_MODES.map(([key, label, help]) => (
                  <label key={key} className="ops-note" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', borderColor: mode === key ? 'var(--accent)' : undefined }}>
                    <input type="radio" name="mode" value={key} checked={mode === key} onChange={() => setMode(key)} style={{ marginTop: 3 }} />
                    <span><strong style={{ display: 'block', color: 'var(--ink)' }}>{label}</strong><span className="ops-help">{help}</span></span>
                  </label>
                ))}
              </div>
            </div>
            {mode === 'full_reanalysis' && (
              <div className="ops-note ops-note--review" style={{ marginTop: 12 }}>
                <label className="ops-check">
                  <input type="checkbox" checked={confirmReanalysis} onChange={e => setConfirmReanalysis(e.target.checked)} />
                  I understand this re-sends every office to the AI model{costCapUsd != null ? ` and stops at the ${'$'}${Number(costCapUsd).toFixed(2)} per-run cost cap` : ''}.
                </label>
              </div>
            )}
            <div className="ops-form-section" style={{ marginTop: 14 }}>
              <h3>Notification</h3>
              <label className="ops-check">
                <input type="checkbox" checked={notifyByEmail} onChange={e => setNotifyByEmail(e.target.checked)} />
                Email the run report to the recipients in Settings when it finishes
              </label>
              <div className="ops-help" style={{ marginTop: 6 }}>Reports list office changes only. They never include patient information.</div>
            </div>
            {error && <div className="ops-error" style={{ marginTop: 12 }}>{error}</div>}
          </Panel>
        </form>
      </div>
    </div>
  );
}
